<template>
  <div>
    <div class="flex justify-between title-y-axis">
      <p class="left">{{ titleY }}</p>
      <p class="right">{{ titleYOpposite }}</p>
    </div>
    <div class="chart-wrapper">
      <apexchart type="line" :options="chartOptions" :series="updatedSeries" height="337" class="mixed-chart-mobile elsa-chart" />
    </div>
  </div>
</template>

<script>
import { toFixed, lensMatching, round } from '../../../utils/ramda-utils'
import { propEq, propOr, compose, converge, reduce, head, identity, view, max, min, curry, map, lensProp, over, sortBy, prop } from 'ramda'
import { DEFAULT_FONT, GRAY_2 } from '@/constants/charts'

const lensByName = compose(lensMatching, propEq('name'))
const maxValue = curry((propName, context) => {
  const maxValue = compose(converge(reduce(max), [head, identity]), propOr([], 'data'), view(lensByName(propName)), propOr([], 'series'))(context)

  return maxValue
})

const minValue = curry((propName, context) => {
  const minValue = compose(converge(reduce(min), [head, identity]), propOr([], 'data'), view(lensByName(propName)), propOr([], 'series'))(context)

  return minValue
})

const yLineScoreColor = '#1ceadf'
const yAxisColor = '#939393'

export default {
  props: {
    series: {
      type: Array,
      default: () => [
        {
          name: 'avg_eps_full',
          type: 'line',
          data: []
        },
        {
          name: 'minutes_practiced',
          type: 'column',
          data: []
        }
      ]
    },
    labels: {
      type: Array,
      default: () => []
    },
    titleY: {
      type: String,
      default: ''
    },
    titleYOpposite: {
      type: String,
      default: ''
    }
  },
  computed: {
    discrete() {
      const ret = []
      if (this.series && this.series.length > 0 && this.series[0].data && this.series[0].data.length > 0) {
        this.series[0].data.forEach((v, k) => {
          if (v === 0) {
            ret.push({
              seriesIndex: 0,
              dataPointIndex: k,
              size: 0
            })
          }
        })
      }
      return ret
    },
    updatedSeries() {
      let series = compose(map(over(lensProp('name'), (name) => this.$t(`charts.${name}`))), propOr([], 'series'))(this)
      return series
    },
    maxNumberOfAvgEps: maxValue('avg_eps_full'),
    maxNumberOfMinutes: maxValue('minutes_practiced'),
    numberOfAvgEpsTickAmount() {
      let ticks = this.maxNumberOfAvgEps < 6 ? round(this.maxNumberOfAvgEps) : 6
      if (ticks <= 6) {
        ticks = 6
      }
      return parseInt(ticks)
    },
    numberOfMinutesTickAmount() {
      let ticks = this.maxNumberOfMinutes < 6 ? round(this.maxNumberOfMinutes) : 6
      if (ticks <= 6) {
        ticks = 6
      }
      return parseInt(ticks)
    },
    chartOptions() {
      return {
        chart: {
          animations: {
            enabled: true
          },
          type: 'line',
          toolbar: {
            show: false
          }
        },
        stroke: {
          width: [4, 0]
        },
        dataLabels: {
          enabled: false,
          enabledOnSeries: [0],
          formatter: function (value) {
            if (value === 0) {
              return ''
            } else {
              return value.toFixed(0)
            }
          },
          offsetY: 0.5,
          background: {
            borderWidth: 0,
            foreColor: '#fff'
          },
          dropShadow: {
            enabled: false
          },
          style: {
            fontSize: '16px',
            fontFamily: DEFAULT_FONT,
            fontWeight: 600
          }
        },
        xaxis: {
          type: 'category',
          categories: this.labels,
          labels: {
            show: true,
            offsetY: 5
          }
        },
        colors: [yLineScoreColor, GRAY_2],
        yaxis: [
          {
            tickAmount: this.numberOfMinutesTickAmount,
            max: 100,
            labels: {
              formatter: toFixed(0),
              // align: 'right',
              style: {
                colors: yLineScoreColor,
                fontSize: '16px',
                fontFamily: DEFAULT_FONT,
                fontWeight: 600,
                cssClass: 'apexcharts-yaxis-label'
              }
            },
            // title: {
            //   text: this.titleY,
            //   align: 'left',
            //   style: {
            //     color: yAxisColor,
            //     fontSize: '16px',
            //     fontFamily: DEFAULT_FONT,
            //     fontWeight: 600
            //   }
            // },
            axisBorder: {
              show: false
            }
          },
          {
            opposite: true,
            tickAmount: this.numberOfAvgEpsTickAmount,
            labels: {
              formatter: toFixed(0),
              align: 'right',
              style: {
                colors: yAxisColor,
                fontSize: '16px',
                fontFamily: DEFAULT_FONT,
                fontWeight: 600
              }
            },
            // title: {
            //   text: this.titleYOpposite,
            //   align: 'left',
            //   style: {
            //     color: yAxisColor,
            //     fontSize: '16px',
            //     fontFamily: DEFAULT_FONT,
            //     fontWeight: 600,
            //     cssClass: 'apexcharts-yaxis-label'
            //   }
            // },
            axisBorder: {
              show: false
            }
          }
        ],
        markers: {
          size: 8,
          strokeWidth: 2,
          fillOpacity: 0,
          strokeOpacity: 0.9,
          strokeColors: yLineScoreColor,
          colors: ['#FFF'],
          discrete: this.discrete,
          hover: {
            sizeOffset: 0
          }
        },
        tooltip: {
          // shared: false,
          y: [
            {
              formatter: (val) => {
                return `${val.toFixed(0)}%`
              },
              title: {
                formatter: (seriesName, ops) => {
                  // return this.$t(
                  //   `classes.classes_details.activity_reports.${seriesName}`
                  // );

                  return this.$t('charts.avg_eps_full')
                }
              }
            },
            {
              formatter: (val) => {
                return `${val.toFixed(0)}`
              },
              title: {
                formatter: (seriesName, ops) => {
                  // return this.$t(
                  //   `classes.classes_details.activity_reports.${seriesName}`
                  // );

                  return this.$t('charts.minutes_practiced')
                }
              }
            }
          ]
        },
        states: {
          hover: {
            filter: {
              type: 'none'
            }
          }
        },
        legend: {
          show: true,
          fontSize: '16px',
          fontFamily: DEFAULT_FONT,
          fontWeight: 400,
          itemMargin: {
            vertical: 15,
            horizontal: 45
          },
          labels: {
            colors: ['#121131']
          },
          markers: {
            fillColors: [[yLineScoreColor], ['#29F4FF', '#5257FF']]
          }
        },
        grid: {
          show: false
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.mixed-chart-mobile {
  min-width: 600px;
}
.elsa-chart {
  margin-top: auto;
  margin-bottom: auto;
  display: flex;
  align-items: center;
  align-content: center;
  flex-direction: row;
}

.chart-wrapper {
  height: 350px;
  display: flex;
  flex-direction: column;
}

::v-deep .apexcharts-yaxis-label {
  font-weight: 600;
}

.title-y-axis {
  margin-bottom: -1.5em;
  font-size: 18px;
  font-weight: 600;
  padding-left: 16px;
  padding-right: 32px;
  .left {
    color: #1ceadf;
  }

  .right {
    color: #939393;
  }
}
</style>
